import Layout from "../../../components/layout/Layout";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PlusIcon, TrashIcon, ChevronLeftIcon, InformationCircleIcon } from "@heroicons/react/solid";
import toast from "react-hot-toast";
import useGet from "../../../hooks/read/useGet";
import usePut from "../../../hooks/usePut";
import Input from "../../../components/elements/Input";
import Label from "../../../components/elements/Label";
import CustomSelect from "../../../components/elements/CustomSelect";
import Loader from "../../../components/Loader";

const FnF = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const navigate = useNavigate();
  /* -------------------------------------------------------------------------- */
  /*                                    STATES                                  */
  /* -------------------------------------------------------------------------- */
  const [payoutOptions, setPayoutOptions] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState(null);

  // Fetch Triggers
  const [enableGratuityFetch, setEnableGratuityFetch] = useState(false);
  const [enableLeaveFetch, setEnableLeaveFetch] = useState(false);
  const [enableBonusFetch, setEnableBonusFetch] = useState(false);

  // Bonus Calculation States
  const [isBonusCalculated, setIsBonusCalculated] = useState(false);
  const [isBonusEligible, setIsBonusEligible] = useState(null); // true, false, or null

  const employeeName = searchParams.get("employee_name");
  const employeeCode = searchParams.get("employee_code");
  const dateOfJoining = searchParams.get("date_of_joining");
  const lastWorkingDateFromUrl = searchParams.get("last_working_date");

  /* -------------------------------------------------------------------------- */
  /*                              API HOOKS (PUT)                               */
  /* -------------------------------------------------------------------------- */
  const { handleAuth } = usePut({
    url: "fnf_create_update",
    onSuccess: (data) => {
      toast.success("FnF data saved successfully!");
      navigate(
        `/employe-details/${id}/edit-fnf?employee_name=${employeeName}&employee_code=${employeeCode}&date_of_joining=${dateOfJoining}`
      );
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to save FnF data");
    },
  });

  /* -------------------------------------------------------------------------- */
  /*                              API HOOKS (GET)                               */
  /* -------------------------------------------------------------------------- */

  // 1. Fetch Payout List
  useGet({
    url: "payout_list",
    initialData: { user_id: id },
    onSuccess: (data) => {
      if (data?.data) {
        setPayoutOptions(data.data);
      }
    },
  });

  // 2. Calculate Gratuity
  const { setGetListParams: setGratuityParams } = useGet({
    url: "calculate_employee_gratuity",
    enabled: enableGratuityFetch,
    onSuccess: (data) => {
      if (data?.data) {
        setValue("total_days", data.data.total_days || 0);
        setValue("present_days", data.data.present_days || 0);
        setValue("absent_days", data.data.absent_days || 0);
        setValue("total_leaves", data.data.total_leaves || 0);
        setValue("no_yrs_of_service", data.data.no_yrs_of_service || 0);
        setValue("period_of_absence_leaves_in_years", data.data.period_of_absence_leaves_in_years || 0);
        setValue("net_no_yrs_of_service", data.data.net_no_yrs_of_service || 0);
        setValue("net_no_yrs_of_service_rounded", data.data.net_no_yrs_of_service_rounded || 0);
        setValue("basic_plus_da", data.data.basic_plus_da || 0);
        setValue("div_26", data.data.div_26 || 0);
        setValue("mul_15", data.data.mul_15 || 0);
        setValue("gratuity_amount", data.data.gratuity_amount || 0);

        toast.success("Gratuity calculated successfully!");
        setEnableGratuityFetch(false);
      }
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to calculate gratuity"
      );
      setEnableGratuityFetch(false);
    },
  });

  // 3. Calculate Leave Balance
  const { setGetListParams: setLeaveParams } = useGet({
    url: "calculate_employee_gratuity",
    enabled: enableLeaveFetch,
    onSuccess: (data) => {
      if (data?.leave_balances) {
        setLeaveBalances(data.leave_balances);
        toast.success("Leave Balance calculated successfully!");
        setEnableLeaveFetch(false);
      }
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to calculate leave balance"
      );
      setEnableLeaveFetch(false);
    },
  });

  // 4. Calculate Bonus
  const { setGetListParams: setBonusParams } = useGet({
    url: "calculate_employee_gratuity",
    enabled: enableBonusFetch,
    onSuccess: (data) => {
      const bonusData = data?.bonus || {};
      setValue("bonus_amount", bonusData.bonus_amount || 0);
      setValue("bonus_days_present", bonusData.no_of_days || 0);
      setIsBonusEligible(bonusData.is_eligible ?? false);
      setIsBonusCalculated(true);

      toast.success("Bonus calculated successfully!");
      setEnableBonusFetch(false);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to calculate bonus");
      setEnableBonusFetch(false);
    },
  });

  /* -------------------------------------------------------------------------- */
  /*                                FORM SETUP                                  */
  /* -------------------------------------------------------------------------- */
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date_of_reg: lastWorkingDateFromUrl
        ? new Date(lastWorkingDateFromUrl)
        : null,
      last_working_date: lastWorkingDateFromUrl
        ? new Date(lastWorkingDateFromUrl)
        : null,
      bonus_date: null,
      bonus_days_present: "",
      bonus_amount: "",
      total_days: 0,
      present_days: 0,
      absent_days: 0,
      total_leaves: 0,
      no_yrs_of_service: 0,
      period_of_absence_leaves_in_years: 0,
      net_no_yrs_of_service: 0,
      net_no_yrs_of_service_rounded: 0,
      basic_plus_da: 0,
      div_26: 0,
      mul_15: 0,
      gratuity_amount: 0,
      final_remarks: "",
      salary_rows: [],
      deduction_rows: [],
    },
  });

  const {
    fields: salaryFields,
    append: appendSalary,
    remove: removeSalary,
  } = useFieldArray({
    control,
    name: "salary_rows",
  });

  const {
    fields: deductionFields,
    append: appendDeduction,
    remove: removeDeduction,
  } = useFieldArray({
    control,
    name: "deduction_rows",
  });

  const bonusAmount = watch("bonus_amount");
  const gratuityFinalPayable = watch("gratuity_amount");
  const salaryRows = watch("salary_rows");
  const deductionRows = watch("deduction_rows");
  const last_working_date = watch("last_working_date");
  const bonus_date = watch("bonus_date");
  const netNoYrsOfService = watch("net_no_yrs_of_service") || 0;
  const mul15Value = watch("mul_15") || 0;

  /* -------------------------------------------------------------------------- */
  /*                             CALCULATIONS                                   */
  /* -------------------------------------------------------------------------- */

  const calculateSalaryTotal = () => {
    return salaryRows.reduce((sum, row) => {
      return sum + (parseFloat(row.pending_amount) || 0);
    }, 0);
  };

  const calculateDeductionTotal = () => {
    return deductionRows.reduce((sum, row) => {
      return sum + (parseFloat(row.deduction_amount) || 0);
    }, 0);
  };

  const calculateLeaveBalanceTotal = () => {
    if (!leaveBalances?.leave_balance) return 0;
    return (
      (parseFloat(leaveBalances.leave_balance.closing_leave_balance_amount) ||
        0) +
      (parseFloat(
        leaveBalances.leave_balance.normal_ot_closing_balance_amount
      ) || 0) +
      (parseFloat(
        leaveBalances.leave_balance.holiday_ot_closing_balance_amount
      ) || 0)
    );
  };

  const calculateFinalTotal = () => {
    const salaryTotal = calculateSalaryTotal();
    const deductionTotal = calculateDeductionTotal();
    const bonusAmt = parseFloat(bonusAmount) || 0;
    const gratuityAmt = parseFloat(gratuityFinalPayable) || 0;
    const leaveBalanceTotal = calculateLeaveBalanceTotal();

    const total = salaryTotal + bonusAmt + gratuityAmt + leaveBalanceTotal - deductionTotal;
    return Math.round(total * 100) / 100;
  };

  const formatDateForSubmission = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  /* -------------------------------------------------------------------------- */
  /*                              HANDLERS                                      */
  /* -------------------------------------------------------------------------- */

  const onSubmit = (formValues) => {
    const formData = {
      emp_fnf_id: "",
      user_id: id,
      date_of_reg: formatDateForSubmission(formValues.date_of_reg),
      last_working_date: formatDateForSubmission(formValues.last_working_date),
      bonus_amount: formValues.bonus_amount ?? "",
      bonus_date: formatDateForSubmission(formValues.bonus_date),
      days_present: formValues.bonus_days_present ?? "",
      is_eligible: isBonusEligible ?? false,
      final_fnf_amount: calculateFinalTotal().toString(),
      total_pending_salary: formValues.salary_rows
        .reduce((sum, row) => sum + (parseFloat(row.pending_amount) || 0), 0)
        .toString(),
      total_deduction: formValues.deduction_rows
        .reduce((sum, row) => sum + (parseFloat(row.deduction_amount) || 0), 0)
        .toString(),
      total_leave_balance_amount: calculateLeaveBalanceTotal().toString(),
      gratuity_total: formValues.gratuity_amount.toString(),
      remarks: formValues.final_remarks,
      emp_fnf_gratuity: {
        emp_fnf_gratuity_id: "",
        total_days: formValues.total_days || 0,
        present_days: formValues.present_days || 0,
        absent_days: formValues.absent_days || 0,
        total_leaves: formValues.total_leaves || 0,
        no_yrs_of_service: formValues.no_yrs_of_service || 0,
        period_of_absence_leaves_in_years: formValues.period_of_absence_leaves_in_years || 0,
        net_no_yrs_of_service: formValues.net_no_yrs_of_service || 0,
        net_no_yrs_of_service_rounded: formValues.net_no_yrs_of_service_rounded || 0,
        basic_plus_da: formValues.basic_plus_da || 0,
        div_26: formValues.div_26 || 0,
        mul_15: formValues.mul_15 || 0,
        gratuity_total: formValues.gratuity_amount.toString(),
        final_payable: formValues.gratuity_amount.toString(),
      },
      emp_fnf_deduction: formValues.deduction_rows.map((row) => ({
        emp_fnf_deduction_id: "",
        deduction_date: formatDateForSubmission(row.deduction_date),
        deduction_amount: row.deduction_amount,
        deduction_remarks: row.deduction_remarks,
      })),
      emp_fnf_pending_salary: formValues.salary_rows.map((row) => ({
        emp_fnf_pending_salary_id: "",
        payout_id: row.payout_id,
        pending_amount: row.pending_amount,
      })),
      emp_fnf_leave_balance: {
        normal_ot_closing_balance:
          leaveBalances?.leave_balance?.normal_ot_closing_balance || "",
        holiday_ot_closing_balance:
          leaveBalances?.leave_balance?.holiday_ot_closing_balance || "",
        closing_leave_balance:
          leaveBalances?.leave_balance?.closing_leave_balance || "",
        closing_leave_balance_amount:
          leaveBalances?.leave_balance?.closing_leave_balance_amount || 0,
        holiday_ot_closing_balance_amount:
          leaveBalances?.leave_balance?.holiday_ot_closing_balance_amount || 0,
        normal_ot_closing_balance_amount:
          leaveBalances?.leave_balance?.normal_ot_closing_balance_amount || 0,
        per_day_salary: leaveBalances?.per_day_salary || 0,
        is_eligible: leaveBalances?.is_eligible ?? false,
      },
    };

    handleAuth.mutate({
      fnf_data: formData,
    });
  };

  const handleInvalidForm = () => {
    toast.error("Fill all required fields");
  };

  // Handler: Gratuity
  const handleCalculateGratuity = () => {
    if (!last_working_date) {
      toast.error(
        "Please select last working date before calculating gratuity"
      );
      return;
    }
    setGratuityParams({
      user_id: id,
      last_working_date: formatDateForSubmission(last_working_date),
      screen: "GRATUITY",
    });
    setEnableGratuityFetch(true);
  };

  // Handler: Leave Balance
  const handleCalculateLeave = () => {
    if (!last_working_date) {
      toast.error(
        "Please select last working date before calculating leave balance"
      );
      return;
    }
    setLeaveParams({
      user_id: id,
      last_working_date: formatDateForSubmission(last_working_date),
      screen: "LEAVE_BALANCE",
    });
    setEnableLeaveFetch(true);
  };

  // Handler: Bonus
  const handleCalculateBonus = () => {
    if (!last_working_date && !bonus_date) {
      toast.error("Please select last working date and bonus date before calculating bonus");
      return;
    }
    if (!last_working_date) {
      toast.error("Please select last working date before calculating bonus");
      return;
    }
    if (!bonus_date) {
      toast.error("Please select bonus date before calculating bonus");
      return;
    }
    setBonusParams({
      user_id: id,
      last_working_date: formatDateForSubmission(last_working_date),
      from_date: formatDateForSubmission(bonus_date), // Required for Bonus
      screen: "BONUS",
    });
    setEnableBonusFetch(true);
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit(onSubmit, handleInvalidForm)}>
        <div className="px-6 py-6 bg-color3 min-h-screen">
          {/* Back Button */}
          <button
            onClick={() => navigate(`/employe-details/${id}`)}
            className="flex items-center hover:underline mb-4"
            type="button"
          >
            <ChevronLeftIcon className="w-6 h-6" />
            <span className="font-semibold">Back</span>
          </button>

          {/* Employee Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h1 className="text-2xl font-bold mb-6 text-color1">
              Full and Final Settlement
            </h1>
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-gray-600 text-sm font-medium">
                  Employee Name
                </p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {employeeName || "----"}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">
                  Employee Code
                </p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {employeeCode || "----"}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">
                  Date Of Joining
                </p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {dateOfJoining || "----"}
                </p>
              </div>
            </div>
          </div>

          {/* Date Selection Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Date of Resignation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Of Resignation<span className="text-red-500">*</span>
                </label>
                <Controller
                  control={control}
                  name="date_of_reg"
                  rules={{
                    required: "Date of resignation is required",
                  }}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div>
                      <DatePicker
                        selected={value}
                        onChange={onChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date"
                        className={`w-full px-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-color1 ${error ? "border-red-500" : "border-gray-300"
                          }`}
                      />
                      {error && (
                        <p className="text-red-600 text-sm mt-1">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              {/* Last Day of Working */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Day of Working<span className="text-red-500">*</span>
                </label>
                <Controller
                  control={control}
                  name="last_working_date"
                  rules={{
                    required: "Last day of working is required",
                  }}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div>
                      <DatePicker
                        selected={value}
                        onChange={onChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date"
                        className={`w-full px-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-color1 ${error ? "border-red-500" : "border-gray-300"
                          }`}
                      />
                      {error && (
                        <p className="text-red-600 text-sm mt-1">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Pending Salary Disbursement Section */}
          <div className="bg-white rounded-lg shadow-md mb-8">
            {/* Header */}
            <div className="flex justify-between items-center bg-color1 text-white px-6 py-4 rounded-t-lg">
              <div className="flex-1 text-center">
                <h2 className="text-lg font-bold">
                  Pending Salary Disbursement
                </h2>
                <p className="text-sm mt-1">Total: ₹{calculateSalaryTotal()}</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  appendSalary({ payout_id: "", pending_amount: "" })
                }
                className="ml-4 px-6 py-2 bg-white text-color1 font-semibold rounded flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                Add
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {salaryFields.length > 0 ? (
                <div className="space-y-4">
                  {salaryFields.map((field, index) => (
                    <div key={field.id}>
                      <div className="grid grid-cols-12 gap-4 items-end pb-4 border-b last:border-b-0">
                        {/* Select Column */}
                        <div className="col-span-5">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select<span className="text-red-500">*</span>
                          </label>
                          <Controller
                            control={control}
                            name={`salary_rows.${index}.payout_id`}
                            rules={{ required: "Month/Year is required" }}
                            render={({ field, fieldState: { error } }) => (
                              <CustomSelect
                                value={field.value}
                                onChange={(val) => {
                                  field.onChange(val);
                                  const selectedPayout = payoutOptions.find(
                                    (p) => p.payout_id === val
                                  );
                                  if (selectedPayout) {
                                    setValue(
                                      `salary_rows.${index}.pending_amount`,
                                      parseFloat(
                                        selectedPayout.net_payable
                                      ).toFixed(2)
                                    );
                                  }
                                }}
                                options={payoutOptions.map((payout) => ({
                                  value: payout.payout_id,
                                  label: payout.month_year,
                                  subLabel: parseFloat(
                                    payout.net_payable
                                  ).toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  }),
                                }))}
                                error={errors.salary_rows?.[index]?.payout_id}
                                placeholder="Select Month/Year"
                              />
                            )}
                          />
                          {errors.salary_rows?.[index]?.payout_id && (
                            <p className="text-red-600 text-sm mt-2">
                              {errors.salary_rows[index].payout_id.message}
                            </p>
                          )}
                        </div>

                        {/* Amount Column */}
                        <div className="col-span-5">
                          <Input
                            control={control}
                            name={`salary_rows.${index}.pending_amount`}
                            label="Amount"
                            type="number"
                            isPositiveNumber
                            disabled={!salaryRows[index]?.payout_id}
                            rules={{ required: "Amount is required" }}
                            parentClassName="mb-0"
                            placeholder="Amount"
                          />
                        </div>

                        {/* Action Column */}
                        <div className="col-span-2 flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeSalary(index)}
                            className="text-red-600 hover:text-red-800 transition p-2"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No rows added yet. Click "Add" to add a row.
                </div>
              )}
            </div>
          </div>

          {/* Bonus Calculation Section */}
          <div className="bg-white rounded-lg shadow-md mb-8">
            {/* Header */}
            {/* Header */}
            {/* Header */}
            <div className="flex justify-between items-center bg-color1 text-white px-6 py-4 rounded-t-lg">
              <div className="flex-1 text-center">
                <h2 className="text-lg font-bold">Bonus Calculation</h2>
              </div>
              <button
                type="button"
                onClick={handleCalculateBonus}
                disabled={enableBonusFetch}
                className="ml-4 px-6 py-2 bg-white text-color1 font-semibold rounded flex items-center gap-2 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enableBonusFetch ? (
                  <>
                    <Loader className="h-5 w-5 !text-color1" />
                    Calculating...
                  </>
                ) : (
                  "Calculate"
                )}
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Eligibility Pill */}
              {isBonusCalculated && isBonusEligible !== null && (
                <div className="flex justify-start mb-6">
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-bold ${isBonusEligible
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                      }`}
                  >
                    {isBonusEligible ? "Eligible" : "Not Eligible"}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-12 gap-4 items-start">
                {/* Date Column */}
                <div className="col-span-4 flex flex-col space-y-2">
                  <Label>Date</Label>
                  <Controller
                    control={control}
                    name="bonus_date"
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center">
                          <DatePicker
                            selected={value}
                            onChange={onChange}
                            dateFormat="dd/MM/yyyy"
                            maxDate={new Date()}
                            wrapperClassName="w-full"
                            className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-color1"
                          />
                        </div>
                        <div className="flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200 w-fit">
                          <InformationCircleIcon className="w-4 h-4" />
                          <span>Select the date from which the bonus is to be calculated</span>
                        </div>
                      </div>
                    )}
                  />
                </div>

                {/* Days Present Column */}
                <div className="col-span-4">
                  <Input
                    control={control}
                    name="bonus_days_present"
                    label="Days Present"
                    type="number"
                    isPositiveNumber
                    isInteger
                    disabled={!isBonusCalculated}
                    placeholder="Days Present"
                    className="text-center"
                  />
                </div>

                {/* Bonus Amount Column */}
                <div className="col-span-4">
                  <Input
                    control={control}
                    name="bonus_amount"
                    label="Bonus Amount"
                    type="number"
                    isPositiveNumber
                    disabled={!isBonusCalculated}
                    placeholder="Bonus Amount"
                    className="text-center"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Gratuity Section */}
          <div className="bg-white rounded-lg shadow-md mb-8">
            {/* Header */}
            <div className="flex justify-between items-center bg-color1 text-white px-6 py-4 rounded-t-lg">
              <div className="flex-1 text-center">
                <h2 className="text-lg font-bold">Gratuity</h2>
              </div>
              <button
                type="button"
                onClick={handleCalculateGratuity}
                disabled={enableGratuityFetch}
                className="ml-4 px-6 py-2 bg-white text-color1 font-semibold rounded flex items-center gap-2 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enableGratuityFetch ? (
                  <>
                    <Loader className="h-5 w-5 !text-color1" />
                    Calculating...
                  </>
                ) : (
                  "Calculate"
                )}
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Gratuity Formula */}
              <div className="mb-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                <p className="font-semibold text-gray-800 mb-2">
                  Gratuity Formula:
                </p>
                <p className="text-gray-700 mb-3">
                  (Base Salary + DA) / 26 * 15 * Net no of years of service
                </p>
                <p className="text-gray-700 text-sm">
                  <span className="font-semibold">
                    Net Number of years of service =
                  </span>{" "}
                  Period of absence of leaves deducted from Total years of service
                </p>
              </div>

              {/* Days Calculation */}
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Days Calculation
              </h3>
              <div className="grid grid-cols-12 gap-4 mb-6">
                {[
                  { name: "total_days", label: "Total Days" },
                  { name: "present_days", label: "Present Days" },
                  { name: "absent_days", label: "Absent Days" },
                  { name: "total_leaves", label: "Total Leaves" },
                  { name: "no_yrs_of_service", label: "No of years of service" },
                  { name: "period_of_absence_leaves_in_years", label: "Period of absence of leaves" },
                  { name: "net_no_yrs_of_service", label: "Net no of years of service" },
                  { name: "net_no_yrs_of_service_rounded", label: "Net no of years of service (rounded off)" },
                ].map((field) => (
                  <div key={field.name} className="col-span-6 lg:col-span-3 flex flex-col justify-between">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                    </label>
                    <Controller
                      control={control}
                      name={field.name}
                      render={({ field: { value } }) => (
                        <input
                          type="number"
                          value={typeof value === "number" ? value : 0}
                          readOnly
                          className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-100 text-gray-700 font-semibold cursor-not-allowed"
                        />
                      )}
                    />
                  </div>
                ))}
              </div>

              {/* Amount Calculation */}
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Amount Calculation
              </h3>
              <div className="grid grid-cols-12 gap-4">
                {[
                  { name: "basic_plus_da", label: "Base Salary + DA" },
                  { name: "div_26", label: "Div 26 (Net Working Days)" },
                  { name: "mul_15", label: "Multiply 15 (Half Month Salary)" },
                  { name: "gratuity_amount", label: `Final Payable (${netNoYrsOfService} * ${mul15Value})` },
                ].map((field) => (
                  <div key={field.name} className="col-span-12 lg:col-span-6 flex flex-col justify-between">
                    <label className="block text-sm font-medium text-gray-700 mb-2 break-words">
                      {field.label}
                    </label>
                    <Controller
                      control={control}
                      name={field.name}
                      render={({ field: { value } }) => (
                        <input
                          type="number"
                          value={typeof value === "number" ? value : 0}
                          readOnly
                          className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-100 text-gray-700 font-semibold cursor-not-allowed"
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Leave Balance Section */}
          <div className="bg-white rounded-lg shadow-md mb-8">
            {/* Header */}
            <div className="flex justify-between items-center bg-color1 text-white px-6 py-4 rounded-t-lg">
              <div className="flex-1 text-center">
                <h2 className="text-lg font-bold">Leave Balance</h2>
                <p className="text-sm mt-1">
                  Total: ₹{calculateLeaveBalanceTotal()}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCalculateLeave}
                disabled={enableLeaveFetch}
                className="ml-4 px-6 py-2 bg-white text-color1 font-semibold rounded flex items-center gap-2 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enableLeaveFetch ? (
                  <>
                    <Loader className="h-5 w-5 !text-color1" />
                    Calculating...
                  </>
                ) : (
                  "Calculate"
                )}
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Eligibility Pill */}
              {leaveBalances?.is_eligible !== undefined && leaveBalances?.is_eligible !== null && (
                <div className="flex justify-start mb-6">
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-bold ${leaveBalances.is_eligible
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                      }`}
                  >
                    {leaveBalances.is_eligible ? "Eligible" : "Not Eligible"}
                  </span>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">
                        Leave Balance Type
                      </th>
                      <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-700">
                        Balance
                      </th>
                      <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-700">
                        Per Day Salary
                      </th>
                      <th className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-800">
                        Closing
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {leaveBalances?.leave_balance?.closing_leave_balance === 0 ? "0" : leaveBalances?.leave_balance?.closing_leave_balance || "-"}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {leaveBalances?.per_day_salary === 0 ? "0" : leaveBalances?.per_day_salary || "-"}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right text-gray-800 font-semibold">
                        {leaveBalances?.leave_balance?.closing_leave_balance_amount === 0 ? "₹0" : leaveBalances?.leave_balance?.closing_leave_balance_amount ? `₹${leaveBalances.leave_balance.closing_leave_balance_amount}` : "-"}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-800">
                        Normal OT
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {leaveBalances?.leave_balance?.normal_ot_closing_balance === 0 ? "0" : leaveBalances?.leave_balance?.normal_ot_closing_balance || "-"}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {leaveBalances?.per_day_salary === 0 ? "0" : leaveBalances?.per_day_salary || "-"}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right text-gray-800 font-semibold">
                        {leaveBalances?.leave_balance?.normal_ot_closing_balance_amount === 0 ? "₹0" : leaveBalances?.leave_balance?.normal_ot_closing_balance_amount ? `₹${leaveBalances.leave_balance.normal_ot_closing_balance_amount}` : "-"}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-800">
                        Holiday OT
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {leaveBalances?.leave_balance?.holiday_ot_closing_balance === 0 ? "0" : leaveBalances?.leave_balance?.holiday_ot_closing_balance || "-"}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                        {leaveBalances?.per_day_salary === 0 ? "0" : leaveBalances?.per_day_salary || "-"}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right text-gray-800 font-semibold">
                        {leaveBalances?.leave_balance?.holiday_ot_closing_balance_amount === 0 ? "₹0" : leaveBalances?.leave_balance?.holiday_ot_closing_balance_amount ? `₹${leaveBalances.leave_balance.holiday_ot_closing_balance_amount}` : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Deduction Section */}
          <div className="bg-white rounded-lg shadow-md mb-8">
            {/* Header */}
            <div className="flex justify-between items-center bg-color1 text-white px-6 py-4 rounded-t-lg">
              <div className="flex-1 text-center">
                <h2 className="text-lg font-bold">Deduction</h2>
                <p className="text-sm mt-1">
                  Total: ₹{calculateDeductionTotal()}
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  appendDeduction({
                    deduction_date: null,
                    deduction_amount: "",
                    deduction_remarks: "",
                  })
                }
                className="ml-4 px-6 py-2 bg-white text-color1 font-semibold rounded flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                Add
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {deductionFields.length > 0 ? (
                <div className="space-y-6">
                  {deductionFields.map((field, index) => (
                    <div key={field.id}>
                      {/* First Row: Deduction Date and Amount */}
                      <div className="grid grid-cols-12 gap-4 mb-4 items-end">
                        {/* Deduction Date Column */}
                        <div className="col-span-6">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Deduction Date
                            <span className="text-red-500">*</span>
                          </label>
                          <Controller
                            control={control}
                            name={`deduction_rows.${index}.deduction_date`}
                            rules={{ required: "Deduction date is required" }}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <div>
                                <DatePicker
                                  selected={value}
                                  onChange={onChange}
                                  dateFormat="dd/MM/yyyy"
                                  maxDate={new Date()}
                                  className={`w-full px-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-color1 ${error ? "border-red-500" : "border-gray-300"
                                    }`}
                                />
                                {error && (
                                  <p className="text-red-600 text-sm mt-2">
                                    {error.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </div>

                        {/* Amount Column */}
                        <div className="col-span-5">
                          <Input
                            control={control}
                            name={`deduction_rows.${index}.deduction_amount`}
                            label="Amount"
                            type="number"
                            isPositiveNumber
                            rules={{ required: "Amount is required" }}
                            parentClassName="mb-0"
                            placeholder="Amount"
                          />
                        </div>

                        {/* Action Column */}
                        <div className="col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeDeduction(index)}
                            className="text-red-600 hover:text-red-800 transition p-2"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Second Row: Remarks */}
                      <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-11">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Remarks
                          </label>
                          <Controller
                            control={control}
                            name={`deduction_rows.${index}.deduction_remarks`}
                            render={({ field }) => (
                              <input
                                {...field}
                                type="text"
                                placeholder="Enter Remarks"
                                className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-color1"
                              />
                            )}
                          />
                        </div>
                      </div>
                      {index !== deductionFields.length - 1 && (
                        <div className="border-b mt-4"></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No rows added yet. Click "Add" to add a row.
                </div>
              )}
            </div>
          </div>

          {/* Final Remarks and Total Section */}
          <div className="bg-white rounded-lg shadow-md mb-8">
            {/* Header */}
            <div className="bg-color1 text-white px-6 py-4 rounded-t-lg text-center">
              <h2 className="text-lg font-bold">Final Settlement Summary</h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Final Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Final Remarks
                  </label>
                  <Controller
                    control={control}
                    name="final_remarks"
                    render={({ field }) => (
                      <textarea
                        placeholder="Enter any final remarks or notes"
                        rows="4"
                        {...field}
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-color1 resize-none"
                      />
                    )}
                  />
                </div>

                {/* Final Total */}
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Final Total
                    </label>
                    <input
                      type="number"
                      value={calculateFinalTotal()}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-100 text-gray-700 font-semibold cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      (Pending Salary + Bonus + Gratuity + Leave Balance - Deductions)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end mb-8">
            <button
              type="submit"
              disabled={handleAuth.isLoading}
              className="px-10 py-3 bg-color1 text-white font-semibold rounded hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {handleAuth.isLoading ? (
                <>
                  <Loader className="h-5 w-5" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default FnF;
